import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTourStore } from '@/stores/tourStore';
import { cn } from '@/lib/utils';
import { setTourReflow } from './tourReflow';
import { tourNavigate } from './tourNavigation';
import { stepTarget, queryVisible, type TourStep, type TourCallout, type TourSide, type TourAlign } from './tourTypes';

/**
 * SpotlightOverlay — the section-spotlight tour renderer (replaces driver.js).
 *
 * Given the resolved step list and the store's `currentStep`, it:
 *   - dims the page with four click-through panels around the target rect, so
 *     the spotlit element(s) stay interactive (type into a field, click a
 *     button) exactly as the old interactive walkthrough did;
 *   - traces the hole with a brand ring;
 *   - renders every callout for a section step AT ONCE, each pinned beside its
 *     field, optional ones badged;
 *   - shows a caption and ONE fixed bottom-center control bar (Back / Next /
 *     progress / Skip) that never moves between steps.
 *
 * It reads `currentStep` from the store and advances it on Next/Back, so resume
 * after a tour modal is trivial — the parent just remounts at `currentStep`.
 */

const PAD = 8; // spotlight padding around the target rect
const CALLOUT_W = 224; // px — must match .tour-callout max-width in index.css
const GAP = 20; // gap between the section and its side-mounted callouts
const POPOVER_W = 300; // px — single-element explanation width (matches .tour-popover)
const POP_GAP = 14; // gap between a single target and its popover

interface PopStyle {
  top: number;
  left: number;
  transform: string;
}

const POPOVER_H_EST = 150; // px — used only for viewport clamping (text got bigger)

/**
 * Choose which side the explanation sits on. HORIZONTAL-FIRST: prefer right,
 * then left, because menus/dropdowns open vertically — a popover beside the
 * control never lands in its expansion path (so it can't cover the dropdown
 * items the user needs to click). Falls back to the step's vertical hint only
 * when there's no horizontal room (e.g. a full-width field). The step's own
 * `side` is honoured when it already points sideways.
 */
function chooseSide(hole: Rect, hinted: TourSide): TourSide {
  const need = POPOVER_W + POP_GAP + 12;
  const vw = window.innerWidth;
  if (hinted === 'left' && hole.left >= need) return 'left';
  if (hinted === 'right' && vw - (hole.left + hole.width) >= need) return 'right';
  if (vw - (hole.left + hole.width) >= need) return 'right';
  if (hole.left >= need) return 'left';
  return hinted === 'top' ? 'top' : 'bottom';
}

/**
 * Position a single-element step's explanation popover beside the spotlit
 * control, clamped into the viewport. The nav bar stays fixed bottom-center
 * regardless — only this descriptive card sits next to the element, so nothing
 * "chases" the user between steps.
 */
function popoverStyle(hole: Rect, side: TourSide, align: TourAlign): PopStyle {
  const cx = hole.left + hole.width / 2;
  const cy = hole.top + hole.height / 2;
  let top: number, left: number, tx = '0', ty = '0';

  if (side === 'left' || side === 'right') {
    left = side === 'right' ? hole.left + hole.width + POP_GAP : hole.left - POP_GAP;
    tx = side === 'right' ? '0' : '-100%';
    top = cy;
    ty = '-50%';
  } else {
    top = side === 'bottom' ? hole.top + hole.height + POP_GAP : hole.top - POP_GAP;
    ty = side === 'bottom' ? '0' : '-100%';
    if (align === 'start') {
      left = hole.left;
    } else if (align === 'end') {
      left = hole.left + hole.width;
      tx = '-100%';
    } else {
      left = cx;
      tx = '-50%';
    }
  }
  // Clamp horizontally.
  const vw = window.innerWidth;
  const halfShift = tx === '-50%' ? POPOVER_W / 2 : tx === '-100%' ? POPOVER_W : 0;
  const leftEdge = left - halfShift;
  if (leftEdge < 12) left += 12 - leftEdge;
  else if (leftEdge + POPOVER_W > vw - 12) left -= leftEdge + POPOVER_W - (vw - 12);
  // Clamp vertically (matters for side placement near the top/bottom edge).
  const vh = window.innerHeight;
  const topShift = ty === '-50%' ? POPOVER_H_EST / 2 : ty === '-100%' ? POPOVER_H_EST : 0;
  const topEdge = top - topShift;
  if (topEdge < 12) top += 12 - topEdge;
  else if (topEdge + POPOVER_H_EST > vh - 96) top -= topEdge + POPOVER_H_EST - (vh - 96); // keep clear of the nav bar
  return { top, left, transform: `translate(${tx}, ${ty})` };
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface CalloutGeom extends TourCallout {
  box: Rect; // viewport coords for the callout box
  n: number; // display number
}

interface Geometry {
  target: Rect | null; // null → centered welcome step (no cutout)
  callouts: CalloutGeom[];
  side: 'right' | 'left' | 'below';
}

function rectOf(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * The rect the spotlight is allowed to draw inside: the viewport, further
 * clipped by every scrollable ancestor of the target.
 *
 * The workspace edit modal tours its tab content from inside a
 * `max-h-[60vh] overflow-y-auto` container. Without this, scrolling that
 * container leaves the target's raw rect wandering outside the modal (or off
 * the page entirely) and the cutout gets drawn over unrelated chrome — the
 * "scroll and the whole thing breaks" failure. Clipping to the container keeps
 * the hole inside the box the user is actually looking at.
 */
function clipRectFor(el: Element): Rect {
  let clip: Rect = { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
  let node = el.parentElement;
  while (node && node !== document.body) {
    const o = getComputedStyle(node);
    const scrolls = /(auto|scroll|hidden)/.test(o.overflowY + o.overflowX);
    if (scrolls) clip = intersect(clip, rectOf(node));
    node = node.parentElement;
  }
  return clip;
}

/** Overlap of two rects. Zero width/height when they don't meet. */
function intersect(a: Rect, b: Rect): Rect {
  const top = Math.max(a.top, b.top);
  const left = Math.max(a.left, b.left);
  const right = Math.min(a.left + a.width, b.left + b.width);
  const bottom = Math.min(a.top + a.height, b.top + b.height);
  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

/** How much of the target is actually on screen, 0–1. */
function visibleFraction(raw: Rect, clip: Rect): number {
  const area = raw.width * raw.height;
  if (area <= 0) return 0;
  const v = intersect(raw, clip);
  return (v.width * v.height) / area;
}

/** Sub-pixel-tolerant rect equality — subpixel jitter must not thrash re-renders. */
function sameRect(a: Rect, b: Rect): boolean {
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

/** Measure the target + callouts for a step against the live DOM. */
function measure(step: TourStep): Geometry {
  const sel = stepTarget(step);
  const targetEl = sel ? queryVisible(sel) : null;
  if (!targetEl) {
    // Welcome / wrap-up step, or the target vanished — centered card, no cutout.
    return { target: null, callouts: [], side: 'below' };
  }
  // Clip to the scroll container so the hole can never be drawn outside the box
  // the target actually lives in (see clipRectFor).
  const target = intersect(rectOf(targetEl), clipRectFor(targetEl));
  const vw = window.innerWidth;

  // Decide which side the callouts live on: right if there's room, else left,
  // else stacked below the section (narrow screens).
  const roomRight = vw - (target.left + target.width + PAD) >= CALLOUT_W + GAP;
  const roomLeft = target.left - PAD >= CALLOUT_W + GAP;
  const side: Geometry['side'] = roomRight ? 'right' : roomLeft ? 'left' : 'below';

  const callouts: CalloutGeom[] = [];
  (step.callouts ?? []).forEach((c, i) => {
    const fieldEl = queryVisible(c.target);
    if (!fieldEl) return; // partial-render: skip callouts whose field is missing
    const f = rectOf(fieldEl);
    let box: Rect;
    if (side === 'right') {
      box = { left: target.left + target.width + PAD + GAP, top: f.top, width: CALLOUT_W, height: 0 };
    } else if (side === 'left') {
      box = { left: target.left - PAD - GAP - CALLOUT_W, top: f.top, width: CALLOUT_W, height: 0 };
    } else {
      // stacked: laid out in the flow below the section (top computed in render)
      box = { left: 0, top: 0, width: CALLOUT_W, height: 0 };
    }
    callouts.push({ ...c, box, n: c.number ?? i + 1 });
  });

  // De-overlap side-mounted callouts: when fields sit close together the boxes
  // would collide, so push each below the previous one. Height is estimated
  // from the description length (~34 chars/line at the callout width).
  if (side !== 'below') {
    // ~30 chars/line at the callout width now the body text is 12.5px, ~18px lines.
    const estHeight = (c: TourCallout) => 44 + Math.ceil((c.description?.length ?? 0) / 30) * 18;
    for (let i = 1; i < callouts.length; i++) {
      const minTop = callouts[i - 1].box.top + estHeight(callouts[i - 1]) + 10;
      if (callouts[i].box.top < minTop) callouts[i].box.top = minTop;
    }
  }

  return { target, callouts, side };
}

export interface SpotlightOverlayProps {
  /** DOM-resolved step list for the active phase. */
  steps: TourStep[];
  /** Current route. Changing it re-measures, so the tour can resume after a detour. */
  routeKey?: string;
  /** Where this phase lives — the paused bar uses it to send the user back. */
  resumeRoute?: string | null;
}

export default function SpotlightOverlay({ steps, routeKey, resumeRoute }: SpotlightOverlayProps) {
  const currentStep = useTourStore((s) => s.currentStep);
  const nextStep = useTourStore((s) => s.nextStep);
  const prevStep = useTourStore((s) => s.prevStep);
  const endTour = useTourStore((s) => s.endTour);
  const openCloseConfirmModal = useTourStore((s) => s.openCloseConfirmModal);
  const restartPhase = useTourStore((s) => s.restartPhase);

  const idx = Math.min(Math.max(currentStep, 0), steps.length - 1);
  const step = steps[idx];
  const isLast = idx >= steps.length - 1;

  const [geom, setGeom] = useState<Geometry>({ target: null, callouts: [], side: 'below' });
  const rafRef = useRef<number | null>(null);
  // Mirror of the measured geometry, readable from interval callbacks without
  // making them a dependency (and without going stale in their closure).
  const geomRef = useRef<Geometry>(geom);
  geomRef.current = geom;

  const recompute = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      if (step) setGeom(measure(step));
    });
  }, [step]);

  // Expose recompute to step callbacks (mobile-menu reflow bridge).
  useEffect(() => {
    setTourReflow(recompute);
    return () => setTourReflow(null);
  }, [recompute]);

  // Pause/resume: watch whether this step's target is still on the page. The
  // user can detour mid-tour ("Manage workspaces" navigates away), and killing
  // the tutorial for that would be brutal. While the target is gone we render
  // nothing; the moment it's back we re-measure and the tour resumes on the
  // SAME step.
  //
  // The same tick also watches whether the target has MOVED. The scroll/resize
  // listeners and the ResizeObserver below catch the target growing or the page
  // scrolling, but NOT the target being repositioned by a sibling's layout
  // change — its size is identical, so the ResizeObserver never fires. That
  // happened for real: choosing a service reflows the controls row and shifts
  // the persona selector sideways, and the cutout stayed on the old spot,
  // leaving the very control the next step says to click sitting under the dim
  // and unclickable. Comparing the rect each tick is one getBoundingClientRect
  // per 400ms, and it makes the spotlight robust to any relayout.
  const [targetPresent, setTargetPresent] = useState(true);
  const prevPresentRef = useRef(true);
  const outOfViewTicksRef = useRef(0);
  useEffect(() => {
    const sel = step ? stepTarget(step) : undefined;
    if (!sel) {
      prevPresentRef.current = true;
      setTargetPresent(true);
      return;
    }
    const check = () => {
      const el = queryVisible(sel);
      const present = el !== null;
      if (present !== prevPresentRef.current) {
        prevPresentRef.current = present;
        setTargetPresent(present);
        if (present) recompute(); // came back — re-measure and resume
        return;
      }
      if (!el) return;

      // Scrolled out of its container? Bring it back rather than leaving the
      // user staring at a dimmed page with the spotlight nowhere to be seen.
      // Requires TWO consecutive out-of-view ticks so we never yank the page
      // while the user is still mid-scroll — only once they've come to rest.
      const raw = rectOf(el);
      if (visibleFraction(raw, clipRectFor(el)) < 0.5) {
        outOfViewTicksRef.current += 1;
        if (outOfViewTicksRef.current >= 2) {
          outOfViewTicksRef.current = 0;
          el.scrollIntoView({ block: 'center', inline: 'nearest' });
        }
        return;
      }
      outOfViewTicksRef.current = 0;

      // Still here and visible — but has it drifted from where we drew the hole?
      const drawn = geomRef.current.target;
      const now = intersect(raw, clipRectFor(el));
      if (!drawn || !sameRect(now, drawn)) recompute();
    };
    check();
    const id = window.setInterval(check, 400);
    return () => window.clearInterval(id);
  }, [step, recompute]);

  // Flag the document while the spotlight is up. CSS uses this to lift OPEN
  // dropdowns above the dim — Radix Selects portal into <body> and our custom
  // menus are absolutely positioned, so both would otherwise render under the
  // dim: greyed out AND unclickable, making a hands-on step impossible.
  useEffect(() => {
    document.body.classList.add('tour-active');
    return () => document.body.classList.remove('tour-active');
  }, []);

  // On step enter: fire the step's onHighlightStarted, scroll the target into
  // view, then measure. A short settle lets scroll/late mounts commit.
  useLayoutEffect(() => {
    if (!step) return;
    step.onHighlightStarted?.();
    const sel = stepTarget(step);
    const el = sel ? queryVisible(sel) : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    setGeom(measure(step));
    const t1 = window.setTimeout(recompute, 120);
    const t2 = window.setTimeout(recompute, 360);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // routeKey: re-measure after a navigation so a paused tour resumes on return.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, steps, routeKey]);

  // Keep geometry in sync while the page scrolls / resizes / the target grows.
  useEffect(() => {
    const onChange = () => recompute();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    let ro: ResizeObserver | null = null;
    const sel = step ? stepTarget(step) : undefined;
    const el = sel ? queryVisible(sel) : null;
    if (el && 'ResizeObserver' in window) {
      ro = new ResizeObserver(onChange);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
      ro?.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [recompute, step]);

  // ---- hands-on action gate --------------------------------------------
  // A step with an `action` blocks Next until the user actually performs it.
  // If the action is ALREADY satisfied when the step opens, we don't auto-skip
  // — we just enable Next so the user still gets the explanation.
  const [actionDone, setActionDone] = useState(false);
  const alreadyDoneRef = useRef(false);
  const nextRef = useRef<() => void>(() => {});

  useEffect(() => {
    const action = step?.action;
    if (!action) {
      alreadyDoneRef.current = false;
      setActionDone(false);
      return;
    }
    const already = action.done();
    alreadyDoneRef.current = already;
    setActionDone(already);
    if (already) return; // nothing to wait for
    const id = window.setInterval(() => {
      if (action.done()) setActionDone(true);
    }, 300);
    return () => window.clearInterval(id);
  }, [step]);

  // Once the user completes the required action, advance for them — that's what
  // makes it feel like a game tutorial rather than a form.
  useEffect(() => {
    if (!step?.action || !actionDone) return;
    if (alreadyDoneRef.current) return; // pre-satisfied → let them click Next
    const t = window.setTimeout(() => nextRef.current(), 600);
    return () => window.clearTimeout(t);
  }, [actionDone, step]);

  if (!step) return null;

  const pop = step.popover ?? {};

  const handleNext = () => {
    if (pop.onNextClick) {
      pop.onNextClick(); // terminal: ends or hands off the tour
      return;
    }
    if (isLast) {
      // Fallback only — every phase's real last step carries an explicit
      // terminal onNextClick. Default to endTour(FALSE) so a phase whose
      // intended last step was DOM-guarded out (e.g. an optional section) can
      // never silently persist tutorial completion and break the phase chain.
      // Completion is always persisted explicitly (Skip / Checkpoint / EndScreen).
      void endTour(false);
      return;
    }
    nextStep();
  };
  const handlePrev = () => {
    pop.onPrevClick?.();
    if (idx > 0) prevStep();
  };
  // Assigned BEFORE the paused return below: a step whose action is completed by
  // NAVIGATING (e.g. "click Manage workspaces") unmounts its own target, so the
  // auto-advance fires while we're technically paused — it still has to chain.
  nextRef.current = handleNext;

  // Gate is pending while the step demands an action the user hasn't done yet.
  const gatePending = !!step.action && !actionDone;

  // PAUSED: the step's target isn't on this page. Don't spotlight anything, but
  // don't vanish either — a tour that silently disappears reads as broken. Keep
  // the phase/step and offer a way back.
  if (stepTarget(step) && (!targetPresent || !geom.target)) {
    // There is ALWAYS a way back. Previously the resume button only rendered when
    // the phase had a route to navigate to — so for a phase that lives on this
    // same page (the workspace sub-tours, which run inside a modal), closing that
    // modal left the user on "tutorial paused" with nothing but "skip tour" and no
    // way to carry on. If we're already where the phase lives, restart it in place;
    // the page's trigger re-opens the modal and picks the chapter back up.
    const canNavigateBack = !!resumeRoute && window.location.pathname !== resumeRoute;
    return createPortal(
      <div className="tour-paused" role="status">
        <span className="tour-paused-label">
          tutorial paused · step {idx + 1} of {steps.length}
        </span>
        <button
          type="button"
          className="tour-nav-next"
          onClick={() => (canNavigateBack ? tourNavigate(resumeRoute!) : restartPhase())}
        >
          resume →
        </button>
        <button type="button" className="tour-nav-skip" onClick={openCloseConfirmModal}>
          skip tour
        </button>
      </div>,
      document.body,
    );
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const t = geom.target;

  // Cutout geometry (viewport coords), padded.
  const hole = t
    ? { top: t.top - PAD, left: t.left - PAD, width: t.width + 2 * PAD, height: t.height + 2 * PAD }
    : null;

  const overlay = (
    <div className="tour-overlay-root" aria-live="polite">
      {hole ? (
        <>
          {/* four click-through dimming panels around the hole */}
          <div className="tour-dim" style={{ top: 0, left: 0, width: vw, height: Math.max(0, hole.top) }} />
          <div
            className="tour-dim"
            style={{ top: hole.top + hole.height, left: 0, width: vw, height: Math.max(0, vh - (hole.top + hole.height)) }}
          />
          <div
            className="tour-dim"
            style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height }}
          />
          <div
            className="tour-dim"
            style={{ top: hole.top, left: hole.left + hole.width, width: Math.max(0, vw - (hole.left + hole.width)), height: hole.height }}
          />
          {/* RAILS: only steps that explicitly opt in are sealed — those whose
           * spotlit control exposes a way OFF the journey (the workspace
           * switcher's menu). Everything else stays operable: sealing every
           * informational step also killed "pick a persona" and onboarding's own
           * Back/Continue buttons, which sit inside the highlighted card. */}
          {step.sealed && (
            <div
              className="tour-seal"
              style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
            />
          )}
          {/* Ring tracing the hole. Its COLOUR is the rule: violet + pulsing means
           * "your turn, do this"; calm slate means "just read this, then Next".
           * Every step used to draw the same violet ring, so there was no way to
           * tell a hands-on step from an informational one — which is what made the
           * rails feel arbitrary (sometimes you click the thing, sometimes you click
           * Next, and the tour looked identical either way). */}
          <div
            className={cn('tour-ring', gatePending ? 'tour-ring--action' : 'tour-ring--info')}
            style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
          />
        </>
      ) : (
        // welcome / wrap-up: a single full dim, centered content card
        <div className="tour-dim" style={{ inset: 0, width: vw, height: vh, left: 0, top: 0 }} />
      )}

      {/* callouts — all shown at once, pinned beside each field (or stacked) */}
      {geom.side !== 'below' &&
        geom.callouts.map((c) => (
          <div
            key={c.target}
            className="tour-callout"
            style={{ top: c.box.top, left: c.box.left, width: c.box.width }}
          >
            <span className="tour-callout-num">{c.n}</span>
            <div className="tour-callout-body">
              <b>
                {c.title}
                {c.optional && <span className="tour-callout-opt">optional</span>}
              </b>
              {c.description && <span>{c.description}</span>}
            </div>
          </div>
        ))}

      {/* narrow-screen fallback: stack callouts in a column near the section */}
      {geom.side === 'below' && geom.callouts.length > 0 && hole && (
        <div
          className="tour-callout-stack"
          style={{ top: hole.top + hole.height + 12, left: Math.max(12, hole.left) }}
        >
          {geom.callouts.map((c) => (
            <div key={c.target} className="tour-callout">
              <span className="tour-callout-num">{c.n}</span>
              <div className="tour-callout-body">
                <b>
                  {c.title}
                  {c.optional && <span className="tour-callout-opt">optional</span>}
                </b>
                {c.description && <span>{c.description}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Copy placement:
       *  - welcome step (no cutout) → centered card;
       *  - single-target step → a popover anchored beside the element (the nav
       *    still stays fixed bottom-center, so only this card sits by the
       *    target — nothing chases the user);
       *  - section step (has callouts) → NO floating copy: the spotlit card
       *    carries its own heading and the callouts explain each field. */}
      {!hole ? (
        <div className="tour-welcome-card">
          {pop.title && <b>{pop.title}</b>}
          {pop.description && <p>{pop.description}</p>}
        </div>
      ) : (
        geom.callouts.length === 0 &&
        (pop.title || pop.description) && (
          <div
            className="tour-popover"
            style={popoverStyle(hole, chooseSide(hole, pop.side ?? 'bottom'), pop.align ?? 'center')}
          >
            {pop.title && <b>{pop.title}</b>}
            {pop.description && <span>{pop.description}</span>}
          </div>
        )
      )}

      {/* fixed bottom-center control bar — one place, every step */}
      <div className="tour-nav" role="group" aria-label="Tour navigation">
        <span className="tour-nav-prog">
          Step {idx + 1} of {steps.length}
        </span>
        <div className="tour-nav-dots" aria-hidden="true">
          {steps.map((_, i) => (
            <i key={i} className={i === idx ? 'on' : undefined} />
          ))}
        </div>
        {idx > 0 && !gatePending && (
          <button type="button" className="tour-nav-back" onClick={handlePrev}>
            {pop.prevBtnText ?? '← back'}
          </button>
        )}
        {gatePending ? (
          // Hands-on step: no Next until they actually do it.
          <span className="tour-nav-todo">
            <i className="tour-nav-todo-dot" />
            {step.action!.hint}
          </span>
        ) : pop.choices?.length ? (
          // A fork: the user picks the branch. No Next — the choice IS the answer.
          pop.choices.map((choice) => (
            <button
              key={choice.label}
              type="button"
              className={choice.subtle ? 'tour-nav-back' : 'tour-nav-next'}
              onClick={choice.onSelect}
            >
              {choice.label}
            </button>
          ))
        ) : (
          <button type="button" className="tour-nav-next" onClick={handleNext}>
            {pop.nextBtnText ?? (isLast ? 'done' : 'next →')}
          </button>
        )}
        <button type="button" className="tour-nav-skip" onClick={openCloseConfirmModal}>
          skip tour
        </button>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
